import store from '../../../store/store';
import { all, put, takeLatest, call, select } from 'redux-saga/effects';
import {
	componentKey,
	setPatientData,
	setSelectedBundle,
	setBundlesData,
	setBundlePushResponse,
	setBundlePushError,
	setConsentList,
	setBundleSaveResponse,
} from './PatientDashboardSlice';
import PatientDataService from '../../../services/PatientDataServices';
import { addNotifications } from '../../../components/common-components/toaster/ToasterSlice';
import { TOASTER_VARIANT } from '../../../components/common-components/toaster/MetToaster';

export const {
	getPatientDetails,
	createDiagnosticBundle,
	createDischargeSummaryBundle,
	createHealthDocumentBundle,
	createOpConsultationBundle,
	createPrescriptionBundle,
	createWellnessBundle,
	createImmunizationBundle,
	createInvoiceBundle,
	pushBundle,
	retryDataPush,
	saveBundle,
	consentIdRequest,
	consentRequestStatus,
	getConsentList,
	getConsentRecordsList,
	getDecryptedData,
} = {
	getPatientDetails: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/PATIENTDETAILS',
			payload,
		};
	},
	createDiagnosticBundle: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/CREATE_DIAGNOSTIC_BUNDLE',
			payload,
		};
	},
	createDischargeSummaryBundle: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/CREATE_DISCHARGE_SUMMARY_BUNDLE',
			payload,
		};
	},
	createHealthDocumentBundle: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/CREATE_HEALTH_DOCUMENT_BUNDLE',
			payload,
		};
	},
	createOpConsultationBundle: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/CREATE_OP_CONSULTATION_BUNDLE',
			payload,
		};
	},
	createPrescriptionBundle: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/CREATE_PRESCRPTION_BUNDLE',
			payload,
		};
	},
	createWellnessBundle: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/CREATE_WELLNESS_BUNDLE',
			payload,
		};
	},
	createImmunizationBundle: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/IMMUNIZATION_BUNDLE',
			payload,
		};
	},
	createInvoiceBundle: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/INVOICE_BUNDLE',
			payload,
		};
	},
	pushBundle: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/PUSH_BUNDLE',
			payload,
		};
	},
	retryDataPush: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/RETRY_DATA_PUSH',
			payload,
		};
	},
	saveBundle: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/SAVE_BUNDLE',
			payload,
		};
	},
	consentIdRequest: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/CONSENT_ID_REQUEST',
			payload,
		};
	},
	consentRequestStatus: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/CONSENT_REQUEST_STATUS',
			payload,
		};
	},
	getConsentList: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/GET_CONSENT_LIST',
			payload,
		};
	},
	getConsentRecordsList: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/GET_CONSENT_RECORDS_LIST',
			payload,
		};
	},
	getDecryptedData: (payload) => {
		return {
			type: 'PATIENTDASHBOARD/GET_DECRYPTED_DATA',
			payload,
		};
	},
};

function* getPatientDetailsAsync(action) {
	try {
		const { patientId = '', page = '1', limit = '10' } = action.payload;
		const params = {
			page: page,
			limit: limit,
		};

		const response = yield PatientDataService.getPatientDetails(patientId, params);

		const { message, status, data } = response?.data || {};
		if (status) {
			yield put(setPatientData(data));
		}
	} catch (error) {
		console.log('err: ', error);
	}
}

function* createDiagnosticBundleAsync(action) {
	try {
		const payload = action.payload;
		const response = yield PatientDataService.createDiagnosticBundle(payload);
		if (response.status === 201) {
			yield put(setSelectedBundle(response.data));
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrong!';
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}

function* createPrescriptionBundleAsync(action) {
	try {
		const payload = action.payload;
		const response = yield PatientDataService.createPrescriptionBundle(payload);
		if (response.status === 201) {
			yield put(setSelectedBundle(response.data));
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrong!';
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}

function* createDischargeSummaryBundleAsync(action) {
	try {
		const payload = action.payload;
		const response = yield PatientDataService.createDischargeSummaryBundle(payload);
		if (response.status === 201) {
			yield put(setSelectedBundle(response.data));
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrong!';
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}

function* createHealthDocumentBundleAsync(action) {
	try {
		const payload = action.payload;
		const response = yield PatientDataService.createHealthDocumentBundle(payload);
		if (response.status === 201) {
			yield put(setSelectedBundle(response.data));
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrong!';
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}

function* createOpConsultationBundleAsync(action) {
	try {
		const payload = action.payload;
		const response = yield PatientDataService.createOpConsultationBundle(payload);
		if (response.status === 201) {
			yield put(setSelectedBundle(response.data));
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrong!';
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}

function* createWellnessBundleAsync(action) {
	try {
		const payload = action.payload;
		const response = yield PatientDataService.createWellnessBundle(payload);
		if (response.status === 201) {
			yield put(setSelectedBundle(response.data));
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrong!';
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}
function* createImmunizationBundleAsync(action) {
	try {
		const payload = action.payload;
		const response = yield PatientDataService.createImmunizationBundle(payload);
		if (response.status === 201) {
			yield put(setSelectedBundle(response.data));
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrong!';
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}
function* createInvoiceBundleAsync(action) {
	try {
		const payload = action.payload;
		const response = yield PatientDataService.createInvoiceBundle(payload);
		if (response.status === 201) {
			yield put(setSelectedBundle(response.data));
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrong!';
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}

function* pushBundleAsync(action) {
	try {
		const payload = action.payload;
		const response = yield PatientDataService.pushBundle(payload);
		if (response.status === 201) {
			yield put(setBundlePushResponse(response.data));
			// yield put(addNotifications({ message: "Bundle linked successfully with ABHA" , variant: TOASTER_VARIANT.SUCCESS }));
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrong!';
		yield put(setBundlePushError(error?.response?.data));
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}

function* retryDataPushAsync(action) {
	try {
		const payload = action.payload;
		const response = yield PatientDataService.retryDataPush(payload);
		if (response.status === 201) {
			yield put(setBundlePushResponse(response.data));
			// yield put(addNotifications({ message: "Bundle linked successfully with ABHA" , variant: TOASTER_VARIANT.SUCCESS }));
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrong!';
		yield put(setBundlePushError(error?.response?.data));
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}

function* saveBundleAsync(action) {
	try {
		const payload = action.payload;
		const response = yield PatientDataService.saveBundle(payload);
		if (response.status === 201) {
			yield put(setBundleSaveResponse(response.data));
            yield put(addNotifications({ message: "Health Record Save Successfully" , variant: TOASTER_VARIANT.SUCCESS }));
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrong!';
		yield put(setBundleSaveResponse(error?.response?.data));
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}

function* consentIdRequestAsync(action) {
	try {
		const payload = action.payload;
		const response = yield PatientDataService.consentIdRequest(payload);
		if (response.status === 201) {
			// yield put(addNotifications({ message: "Consent ID created successfully" , variant: TOASTER_VARIANT.SUCCESS }));
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrong!';
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}

function* consentRequestStatusAsync(action) {
	try {
		const payload = action.payload;
		const response = yield PatientDataService.consentRequestStatus(payload);
		// if(response.status === 201){
		//     yield put(addNotifications({ message: "Consent status requested successfully" , variant: TOASTER_VARIANT.SUCCESS }));
		// }
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrong!';
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}

function* getConsentListAsync(action) {
	try {
		const data = action.payload;
		const response = yield PatientDataService.getConsentList(data);
		if (response.status === 200) {
			yield put(setConsentList(response.data));
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrong!';
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}

function* getConsentRecordsListAsync(action) {
	try {
		const { id } = action.payload;
		const response = yield PatientDataService.getConsentRecordsList(id);
		if (response.status === 200) {
			const records = response.data?.consent?.consents_data.map((data) => data.healthData);
			yield put(setBundlesData(records));
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrongg!';
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}

function* getDecryptedDataAsync(action) {
	try {
		const {patientId, decryptedData } = action.payload;
		const allResponses = [];
		for (const item of decryptedData) {
			const entries = item.entries || [];
			const payloadData = {
				senderNonce: item?.keyMaterial?.nonce,
				senderPublicKey: item?.keyMaterial?.dhPublicKey?.keyValue,
				patientId,
			};

			const responses = yield all(
				entries.map((entry) =>
					call(function* () {
						try {
							const response = yield call(PatientDataService.getDecryptedData, {
								...payloadData,
								encryptedData: entry.content,
							});
							return { status: 'fulfilled', value: response.data };
						} catch (err) {
							return { status: 'rejected', reason: err };
						}
					})
				)
			);

			const fulfilled = responses.filter((r) => r.status === 'fulfilled').map((r) => r.value);
			allResponses.push(...fulfilled);
		}

		if (allResponses.length > 0) {
			yield put(setBundlesData(allResponses));
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || 'Something went wrong!';
		yield put(addNotifications({ message: errorMessage, variant: TOASTER_VARIANT.ERROR }));
		console.log('err:', error);
	}
}

function* rootSaga() {
	yield all([
		takeLatest(getPatientDetails().type, getPatientDetailsAsync),
		takeLatest(createDiagnosticBundle().type, createDiagnosticBundleAsync),
		takeLatest(createDischargeSummaryBundle().type, createDischargeSummaryBundleAsync),
		takeLatest(createHealthDocumentBundle().type, createHealthDocumentBundleAsync),
		takeLatest(createOpConsultationBundle().type, createOpConsultationBundleAsync),
		takeLatest(createPrescriptionBundle().type, createPrescriptionBundleAsync),
		takeLatest(createWellnessBundle().type, createWellnessBundleAsync),
		takeLatest(createImmunizationBundle().type, createImmunizationBundleAsync),
		takeLatest(createInvoiceBundle().type, createInvoiceBundleAsync),
		takeLatest(pushBundle().type, pushBundleAsync),
		takeLatest(retryDataPush().type, retryDataPushAsync),
		takeLatest(saveBundle().type, saveBundleAsync),
		takeLatest(consentIdRequest().type, consentIdRequestAsync),
		takeLatest(consentRequestStatus().type, consentRequestStatusAsync),
		takeLatest(getConsentList().type, getConsentListAsync),
		takeLatest(getConsentRecordsList().type, getConsentRecordsListAsync),
		takeLatest(getDecryptedData().type, getDecryptedDataAsync),
	]);
}

store.sagaManager.addSaga(componentKey, rootSaga);
